/**
 * parentLanguage.js - the chart in a parent's words.
 *
 * WHY THIS FILE EXISTS
 * framework.js names every box for the three professional audiences the spec
 * serves - "Attempts Vocalization", "Verb Morphology", "Contextually
 * Appropriate". Those names are correct and they are not for a parent. This is
 * the one place they are translated, so a phrase a parent reads about their own
 * child is written down deliberately rather than assembled in a component.
 *
 * RULES, from CLAUDE.md section 5:
 *   - Plain, warm, short. No slang. No clinical vocabulary.
 *   - Nothing here may read as failure. There is no fail state in this product
 *     and there is none on this chart either.
 *   - Never "regression", never "declined", never "lost". A box whose recent
 *     plays have slipped is an INVITATION TO PLAY - "Ready for another go" -
 *     because that is a thing a parent can act on, and "your child has
 *     regressed" is a thing that just hurts.
 *
 * Keyed by phase and component code, which are permanent (framework.js
 * makeBoxId). Adding a phase means adding its components here; `boxLabel()`
 * falls back to the framework's own name rather than showing nothing.
 */

/* =========================================================================
 * THE 45 COMPONENTS
 * `short` goes under a box in the detail sheet; `means` is the sentence that
 * explains it. Both are the parent's, not the clinician's.
 * ========================================================================= */

export const COMPONENT_LANGUAGE = {
  1: {
    A: { short: 'Watching and listening', means: 'Looks at the screen and listens when the word is said.' },
    B: { short: 'Making a sound',         means: 'Answers with a sound of any kind. It does not have to be the word.' },
    C: { short: 'Saying the word',        means: 'Says the word clearly enough to be recognised.' },
  },
  2: {
    A: { short: 'Answering with less help', means: 'Answers when only part of the clue is given.' },
    B: { short: 'Remembering the word',     means: 'Finds the word from memory, without hearing it first.' },
    C: { short: 'Saying it on their own',   means: 'Says the word with no help at all.' },
  },
  3: {
    A: { short: 'The doing word',          means: 'Uses the action word - push, want, open.' },
    B: { short: 'The naming word',         means: 'Uses the right name for the thing.' },
    C: { short: 'Two words together',      means: 'Puts the two words in the right order.' },
    D: { short: 'Saying it without copying', means: 'Says it themselves rather than repeating what they just heard.' },
  },
  4: {
    A: { short: 'Adding -ing',            means: 'Says running, eating, jumping - not just run, eat, jump.' },
    B: { short: 'Little position words',  means: 'Uses in, on, under, next to.' },
    C: { short: 'Adding the and a',       means: 'Uses the small joining words in front of things.' },
    D: { short: 'Putting it all together', means: 'Uses those pieces together in one go.' },
  },
  5: {
    A: { short: 'Who it is about',        means: 'Starts with I, you or it.' },
    B: { short: 'The doing word',         means: 'Uses the action word in the middle.' },
    C: { short: 'Finishing the phrase',   means: 'Adds the last part so the phrase is complete.' },
    D: { short: 'In the right order',     means: 'Puts the three parts in an order that makes sense.' },
  },
  6: {
    A: { short: 'A whole sentence',       means: 'Says a full sentence, not just the important words.' },
    B: { short: 'Getting the bits right', means: 'The small words and endings are right.' },
    C: { short: 'Fits what is happening', means: 'The sentence matches what is going on.' },
    D: { short: 'Without being shown',    means: 'Says it without hearing it first.' },
  },
  7: {
    A: { short: 'The first instruction',  means: 'Gives a clear first instruction.' },
    B: { short: 'The second instruction', means: 'Gives a clear second one.' },
    C: { short: 'Joining words',          means: 'Uses then, and, after to link them.' },
    D: { short: 'In a sensible order',    means: 'The instructions come in an order that would work.' },
    E: { short: 'The third instruction',  means: 'Holds on to a third one as well.' },
  },
  8: {
    A: { short: 'Listening to the question', means: 'Takes in the question before answering.' },
    B: { short: 'Understanding it',          means: 'Answers the kind of question that was asked.' },
    C: { short: 'Thinking of an answer',     means: 'Comes up with something that fits.' },
    D: { short: 'Saying it clearly',         means: 'Gets the answer out so it can be understood.' },
    E: { short: 'Staying on the subject',    means: 'Keeps to what you were both talking about.' },
  },
  9: {
    A: { short: 'Starting it off',        means: 'Says something first, without being asked.' },
    B: { short: 'Fitting the moment',     means: 'What they say suits what is happening.' },
    C: { short: 'Easy to understand',     means: 'The message comes across.' },
    D: { short: 'Keeping it going',       means: 'Carries on the back and forth.' },
  },
  10: {
    A: { short: 'Saying something new',   means: 'Puts words together in their own way.' },
    B: { short: 'Lots of different words', means: 'Reaches for more than the same few words.' },
    C: { short: 'Grammar that fits',      means: 'The way it is put together suits the moment.' },
    D: { short: 'Using it elsewhere',     means: 'Does the same thing in a situation they have not practised.' },
  },
};

/* =========================================================================
 * THE STATES
 *
 * Six colours and three non-colours. The non-colours are the ones that get
 * this wrong most easily: grey currently means two different things in the old
 * mockup, and neither of them is failure.
 * ========================================================================= */

export const STATE_LANGUAGE = {
  green:  { label: 'Got it',          blurb: 'Right first time in nearly every go.' },
  purple: { label: 'Nearly there',    blurb: 'Right first time most times.' },
  blue:   { label: 'Getting there',   blurb: 'Right first time about half the time.' },
  yellow: { label: 'Just started',    blurb: 'Starting to come right.' },
  red:    { label: 'Needs a hand',    blurb: 'Still tricky. The game gives more help here on its own.' },

  // Playing, but not enough goes yet to say anything. This is the ONLY meaning
  // of grey on this chart.
  in_progress: { label: 'Having a go', blurb: 'Not enough goes yet to tell.' },
  not_started: { label: 'Not tried yet', blurb: 'Nothing here yet.' },

  // Permanent. 17 of the 208. A microphone cannot see a child looking at the
  // screen, and no game will ever change that.
  observational: {
    label: 'For you to notice',
    blurb: 'This one is for you to see, not for the game. A microphone cannot tell whether they were looking.',
  },

  // Per-game and fixable, which is the whole point of saying it out loud.
  not_assessable: {
    label: 'Not in your games yet',
    blurb: 'None of the games you have played can measure this one yet.',
  },
};

/** The decay marker. An invitation, never a verdict. */
export const READY_AGAIN = {
  label: 'Ready for another go',
  blurb: 'They had this. The last few goes have been trickier, so it is a good one to come back to.',
};

/* =========================================================================
 * LOOKUPS
 * ========================================================================= */

/**
 * A parent's name for one box.
 * @param {number} phase
 * @param {string} code component letter
 * @param {string} [fallback] the framework's own name
 * @returns {{short:string, means:string}}
 */
export function boxLanguage(phase, code, fallback = '') {
  const row = COMPONENT_LANGUAGE[phase];
  const entry = row && row[code];
  if (entry) return entry;
  // Better the clinical name than a blank. Also the signal that a phase was
  // added to the framework and not to this file.
  return { short: fallback || `Part ${code}`, means: '' };
}

/**
 * A parent's name for a state.
 * @param {string} state
 * @returns {{label:string, blurb:string}}
 */
export function stateLanguage(state) {
  return STATE_LANGUAGE[state] || STATE_LANGUAGE.not_started;
}

/**
 * How a level is described in a list. Levels are "steps" to a parent - "level"
 * reads as a difficulty setting, which is not what it is.
 */
export function stepLabel(level) {
  return `Step ${level}`;
}

/**
 * Plain-language count. "1 skill" not "1 skills", and never "n boxes" - a
 * parent has no idea what a box is and should not have to learn.
 */
export function skillCount(n) {
  return `${n} ${n === 1 ? 'skill' : 'skills'}`;
}

export default {
  COMPONENT_LANGUAGE,
  STATE_LANGUAGE,
  READY_AGAIN,
  boxLanguage,
  stateLanguage,
  stepLabel,
  skillCount,
};
